import { config } from '@/config/env';
import { useAuthStore } from '@/modules/auth/stores/authStore';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(authenticated = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (authenticated) {
      const { accessToken, idToken } = useAuthStore.getState();
      const token = idToken || accessToken;
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async get<T>(endpoint: string, authenticated = true): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(authenticated),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      const error = data as ApiError;
      throw new Error(error.error?.message || 'API 요청 실패');
    }

    return (data as ApiResponse<T>).data;
  }

  async post<T>(endpoint: string, body?: unknown, authenticated = true): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(authenticated),
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      const error = data as ApiError;
      throw new Error(error.error?.message || 'API 요청 실패');
    }

    return (data as ApiResponse<T>).data;
  }

  async put<T>(endpoint: string, body?: unknown, authenticated = true): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(authenticated),
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      const error = data as ApiError;
      throw new Error(error.error?.message || 'API 요청 실패');
    }

    return (data as ApiResponse<T>).data;
  }

  async delete<T>(endpoint: string, authenticated = true): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(authenticated),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      const error = data as ApiError;
      throw new Error(error.error?.message || 'API 요청 실패');
    }

    return (data as ApiResponse<T>).data;
  }
}

export const api = new ApiClient(config.apiUrl);

// Topic Types
export interface Topic {
  topicId: string;
  title: string;
  emoji: string;
  category: string;
  stats: {
    totalDebates: number;
    proWins: number;
    conWins: number;
    draws: number;
  };
}

export interface TopicsResponse {
  topics: Topic[];
  total: number;
}

// Topics API
export const topicsApi = {
  getTopics: (limit = 4) => 
    api.get<TopicsResponse>(`/topics?limit=${limit}`, false),
};
